import { DetailRequirement } from '@cibo/core'
import { Loading, useUpdateUserDetails } from '@cibo/profile'
import {
  PROGRAM_ROUTES,
  RadioGroupFormikInput,
  ResourceDetailFeatureTaskEditorProps,
} from '@cibo/ui'
import {
  Alert,
  Collapse,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  Stack,
  Typography,
} from '@mui/material'
import { Field, Form, Formik, useFormikContext } from 'formik'
import { equals, mergeAll } from 'ramda'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath, Link } from 'react-router-dom'
import * as yup from 'yup'
import { useProgramEngagementContext } from '../../../../context'
import {
  useMultipleProgramEngagementsDetails,
  useProgramEngagementDetails,
  useProgramEngagements,
  useUpdateProgramEngagementDetails,
} from '../../../../queries'
import { UsdaGrowerDetails } from '../UsdaGrowerDetails/types'
import { BUSINESS_STRUCTURE, EqipFarmInformation, FarmInformation } from './types'

export const EqipFarmInformationTaskEditorForm = ({
  detailRequirements,
  onUpdating,
  onError,
  onSuccess,
  subDetailRequirements,
}: EqipFarmInformationTaskEditorProps) => {
  const { t } = useTranslation('@cibo/FarmInformationTaskEditor')
  // @ts-ignore not sure how to tell our types this detail uses value: {...}
  const updateResourceDetail = useUpdateProgramEngagementDetails<EqipFarmInformation>()
  const updateUserDetails = useUpdateUserDetails<UsdaGrowerDetails>()
  const { engagementId, userRole, ownerName, userId, programId } = useProgramEngagementContext()
  const details = useProgramEngagementDetails({ engagementId, detailRequirements })
  const relevantEngagementsQuery = useProgramEngagements({
    filters: [
      {
        programId: [programId],
        userId,
      },
    ],
  })
  const engagementDetails = useMultipleProgramEngagementsDetails<{
    traitId: 'eqipFarmInformation'
    result: { businessStructure: string }
    input: never
  }>({
    resourceIds: relevantEngagementsQuery.data?.items.map(item => item.id),
    detailRequirements,
  })
  const individualEngagementId = engagementDetails.find(engagement => {
    return engagement.data?.detail.find(detail => detail.result?.businessStructure === 'individual')
  })?.data
  const individualEngagement = relevantEngagementsQuery.data?.items.find(
    item => item.id === individualEngagementId?.resourceId
  )

  const year = detailRequirements?.[0]?.year

  const { values, isValid, isValidating, dirty, touched } = useFormikContext()

  useEffect(() => {
    const notDirtyOrTouched = !dirty && equals(touched, {})
    if (notDirtyOrTouched || isValidating || !isValid) return
    const emptyUserSubDetails =
      subDetailRequirements
        ?.filter(a => a.dataType === 'user')
        .map(({ traitId, year }) => ({
          traitId,
          year,
          value: undefined,
        })) || []
    const emptyWorkflowSubDetails =
      subDetailRequirements
        ?.filter(a => a.dataType === 'workflow')
        .map(({ traitId, year }) => ({
          traitId,
          year,
          value: undefined,
        })) || []
    onUpdating?.()
    Promise.all([
      updateResourceDetail.mutateAsync({
        engagementId: engagementId as string,
        details: [
          {
            traitId: 'eqipFarmInformation',
            year,
            value: values,
          } as EqipFarmInformation,
          // @ts-ignore this won't be the FarmInfo detail
          ...emptyWorkflowSubDetails,
        ],
      }),
      updateUserDetails.mutateAsync({
        userId: userId?.toString() as string,
        // @ts-ignore dunno
        details: emptyUserSubDetails,
      }),
    ])
      .then(onSuccess)
      .catch(onError)
  }, [values, isValid, isValidating, engagementId, year, dirty, touched])

  const disabled =
    details.data?.[0]?.immutable || details.isFetching || updateResourceDetail.isPending

  return (
    <Form>
      <Stack spacing={2}>
        <Stack>
          <Typography>{t('title')}</Typography>
          <Typography variant="caption">{t('cta')}</Typography>
        </Stack>

        <Stack>
          <FormLabel required>
            {t('businessStructureCta', { context: userRole, name: ownerName })}
          </FormLabel>

          <FormControl component="fieldset" variant="standard" error={updateResourceDetail.isError}>
            <Field component={RadioGroupFormikInput} row name="businessStructure">
              <FormControlLabel
                value="entity"
                data-testid="option-entity"
                control={<Radio />}
                label={t('businessStructure_entity', { context: userRole, name: ownerName })}
                disabled={disabled}
              />
              <FormControlLabel
                value="individual"
                data-testid="option-individual"
                control={<Radio />}
                label={t('businessStructure_individual', { context: userRole, name: ownerName })}
                disabled={disabled}
              />
            </Field>
          </FormControl>
        </Stack>

        <Collapse in={updateResourceDetail.isError}>
          <FormHelperText>
            {updateResourceDetail.error?.response?.data?.reason ===
            'farmerHasExistingIndividualApplication' ? (
              <Alert sx={{ mt: 3 }} severity="error">
                <Typography>
                  {t('farmerHasExistingIndividualApplication', { name: ownerName })}
                  <Link
                    to={generatePath(PROGRAM_ROUTES.ENROLL_DETAIL, {
                      programId: programId,
                      workflowId: individualEngagement?.id as string,
                    })}
                  >
                    {individualEngagement?.name}
                  </Link>
                </Typography>
              </Alert>
            ) : (
              updateResourceDetail.error?.response?.data?.message
            )}
          </FormHelperText>
        </Collapse>
      </Stack>
    </Form>
  )
}

type EqipFarmInformationTaskEditorProps = Omit<
  ResourceDetailFeatureTaskEditorProps<EqipFarmInformation>,
  'resourceIds' | 'task' | 'engagement'
> & {
  subDetailRequirements?: DetailRequirement<any>[]
}

export const EqipFarmInformationTaskEditor = (props: EqipFarmInformationTaskEditorProps) => {
  const { t } = useTranslation('@cibo/FarmInformationTaskEditor')

  // @ts-ignore not sure how to tell our types this detail uses value: {...}
  const details = useProgramEngagementDetails<EqipFarmInformation>({
    engagementId: props.engagementId,
    detailRequirements: props.detailRequirements,
  })
  const detail = details?.data?.[0]

  const schema = useMemo(
    () =>
      yup.object({
        businessStructure: yup.mixed().oneOf(BUSINESS_STRUCTURE).required(t('required')),
      }),
    [t]
  )

  if (details.isPending) return <Loading />

  const initialValues = mergeAll([{ businessStructure: '' }, detail?.value || {}])

  return (
    <Formik<FarmInformation>
      // @ts-ignore How to allow empty strings for these initial values?
      initialValues={initialValues}
      onSubmit={() => {}}
      validationSchema={schema}
    >
      <EqipFarmInformationTaskEditorForm {...props} />
    </Formik>
  )
}
